/** Team management — list workspace members, add one, change roles, remove. tenant_admin only. */
import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../auth';
import { ApiError, type Member, type Role } from '../api';
import { Banner, Button } from '../ui';

const ROLES: Role[] = ['viewer', 'owner_operator', 'operator', 'engineer', 'tenant_admin'];

export function Team(): JSX.Element {
  const { api } = useAuth();
  const [members, setMembers] = useState<Member[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<Role>('operator');
  const [password, setPassword] = useState('');
  // Revealing is a per-entry decision, never a sticky preference: a page left open on a revealed
  // password is a password on a screen in a workshop.
  const [showPassword, setShowPassword] = useState(false);
  const [adding, setAdding] = useState(false);

  const load = useCallback(() => {
    api
      .listTeam()
      .then((r) => setMembers(r.members))
      .catch((e) => setErr(e instanceof ApiError ? e.message : 'Could not load the team.'));
  }, [api]);
  useEffect(load, [load]);

  async function add(e: React.FormEvent) {
    e.preventDefault();
    setAdding(true);
    setErr(null);
    try {
      await api.addMember({
        email: email.trim(),
        role,
        ...(password ? { password } : {}),
      });
      setEmail('');
      setPassword('');
      setShowPassword(false);
      setRole('operator');
      load();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Could not add the member.');
    } finally {
      setAdding(false);
    }
  }

  async function changeRole(userId: string, next: Role) {
    setErr(null);
    try {
      await api.setMemberRole(userId, next);
      load();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Could not change the role.');
    }
  }

  async function remove(m: Member) {
    if (!window.confirm(`Remove ${m.email} from this workspace?`)) return;
    setErr(null);
    try {
      await api.removeMember(m.user_id);
      load();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Could not remove the member.');
    }
  }

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Team</h1>
          <div className="lead">Who can see and operate this workspace.</div>
        </div>
      </div>

      {err && <Banner kind="err">{err}</Banner>}

      <div className="stack">
        {members?.map((m) => (
          <div className="row-card" key={m.user_id}>
            <div>
              <div className="title">
                {m.email}
                {m.is_you && <span className="faint"> · you</span>}
              </div>
              <div className="meta">{m.status}</div>
            </div>
            <div className="row-right">
              <select
                className="select"
                style={{ width: 'auto' }}
                value={m.role}
                onChange={(e) => changeRole(m.user_id, e.target.value as Role)}
              >
                {ROLES.map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
              </select>
              <button
                className="btn btn-ghost btn-sm"
                onClick={() => remove(m)}
                disabled={m.is_you}
                title={m.is_you ? 'You cannot remove yourself' : 'Remove from workspace'}
              >
                Remove
              </button>
            </div>
          </div>
        ))}
        {members === null && <div className="empty">Loading…</div>}
      </div>

      <div className="panel panel-pad" style={{ marginTop: 20 }}>
        <div className="section-label">Add a member</div>
        <form onSubmit={add}>
          <div className="field row">
            <div>
              <label htmlFor="e">Email</label>
              <input
                id="e"
                className="input"
                type="email"
                placeholder="person@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            <div>
              <label htmlFor="r">Role</label>
              <select
                id="r"
                className="select"
                value={role}
                onChange={(e) => setRole(e.target.value as Role)}
              >
                {ROLES.map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="field">
            <label htmlFor="p">
              Temporary password <span className="opt">(new accounts only)</span>
            </label>
            <div className="pw-wrap">
              <input
                id="p"
                className="input pw-input"
                type={showPassword ? 'text' : 'password'}
                placeholder="at least 12 characters — leave blank for an existing account"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                minLength={12}
              />
              <button
                type="button"
                className="pw-toggle"
                onClick={() => setShowPassword((v) => !v)}
                aria-pressed={showPassword}
                aria-controls="p"
                title={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? 'Hide' : 'Show'}
              </button>
            </div>
          </div>
          <div className="form-actions">
            <span />
            <Button type="submit" busy={adding}>
              Add member
            </Button>
          </div>
        </form>
      </div>
    </>
  );
}
